import type { Comment } from '@/@types/comment'
import { FileAttachment } from '@/utils/fetchers/attachment'
import clsx from 'clsx'
import {
	type ClipboardEvent,
	type ClipboardEventHandler,
	type ReactNode,
	type Ref,
	useRef,
} from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import { filterTransferItems } from '../Attachment/utils'
import LexicalEditor, { type LexicalEditorHandle } from '../Editor/LexicalEditor'
import styles from './CommentContentEditor.module.css'
import { CommentFormValue } from './schema'

export interface CommentContentEditorProps extends Omit<
	ControllerRenderProps<CommentFormValue, 'content'>,
	'ref'
> {
	to: Comment | undefined

	/**
	 * @default false
	 */
	autoFocus?: boolean

	onPaste?: (attachments: FileAttachment[]) => void
	placeholder?: ReactNode
	onSubmit?: () => void
	ref?: Ref<LexicalEditorHandle>
	className?: string
}

export default function CommentContentEditor({
	to: toComment,
	autoFocus = false,
	onPaste,
	placeholder,
	onSubmit,
	ref,
	className,
	...props
}: CommentContentEditorProps) {
	const isCompositingRef = useRef<boolean>(false)
	const isReplyMode = !!toComment

	const handleTextareaPaste: ClipboardEventHandler<HTMLElement> = (
		e: ClipboardEvent<HTMLTextAreaElement>
	) => {
		const dataTransferItems = Array.from(e.clipboardData.items)
		const { images, videos } = filterTransferItems(dataTransferItems)

		onPaste?.(images.concat(videos) as FileAttachment[])
	}

	return (
		<LexicalEditor
			{...props}
			ref={ref}
			spellCheck={false}
			placeholder={
				placeholder ??
				(isReplyMode ? `回复给 ${toComment.user.screenName}...` : '友善发言的人运气都不会太差')
			}
			onPaste={handleTextareaPaste}
			onSubmit={onSubmit}
			data-autofocus={autoFocus}
			onCompositionStart={() => {
				isCompositingRef.current = true
			}}
			onCompositionEnd={() => {
				isCompositingRef.current = false
			}}
			className={clsx(styles.editor, className)}
		/>
	)
}
